import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Moment from 'moment';
import { toast } from 'react-toastify';
import {
    get as _get,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import InfoIcon from '@mui/icons-material/Info';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';

import { ThemeChanger } from '../../cmc/app';
import { MC2ThemeChanger } from '../../mc2/app';
import Validator, { useValidate, checkValidity } from '../../../components/validator';

import Auth, { ProfileContext, User } from '../../../data/auth-service';
import RolesService from '../../../data/roles-service';
import UsersService, { HOME_PAGES } from '../../../data/users-service';

import { TranslateHome, TranslateTheme } from '../../../libraries/helpers';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    select: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        width: '100%',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: 36,
    },
    chip: {
        margin: 2,
        background: theme.palette.secondary.main,
        color: '#FFF',
        fontSize: 16,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255, 255, 255, 0.75)',
        },
    },
}));

const CreateUser = () => {
    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';
    const classes = useStyles();
    const theme = useTheme();
    const { changeTheme, changeDateFormat } = useContext(MC2CHECK ? MC2ThemeChanger : ThemeChanger);
    const location = useLocation();
    const navigate = useNavigate();
    const routeParams = useParams();
    const [isUpdate, setIsUpdate] = useState(false);
    const [user, setUser] = useState({
        data: { profile: { home: '/', colorscheme: 'light' } },
        email: '',
        name: '',
        roles: [],
        username: '',
        type: '',
    });
    const [roles, setRoles] = useState([]);
    const userForm = useValidate({ roleSelect: false });
    // this page is always available for self - but the roles list isn't
    const { updateProfile } = useContext(ProfileContext);
    const [themes, setThemes] = useState(['Light', 'Dark']);
    const [init, setInit] = useState(true);
    const [valid, setValid] = useState(false);
    const [changes, setChanges] = useState(false);
    const [OGUser, setOGUser] = useState(false);
    const userPerms = Auth.permissions.get('Users');
    const accessKeysAuthPerms = Auth.permissions.get('AccessKeys');

    const wantRoles = useMemo(() => {
        return user.username !== 'admin' && (!isUpdate || user.type === 'brc');
    }, [user.username, user.type, isUpdate]);

    useEffect(() => {
        if (routeParams.username) {
            setIsUpdate(true);
            UsersService.getUser(routeParams.username).then((res) => {
                res.object.data.profile.colorscheme = TranslateTheme(res.object.data.profile.colorscheme);
                res.object.data.profile.home = TranslateHome(res.object.data.profile.home);
                setUser(res.object);
                setOGUser(_cloneDeep(res.object));
                if (_get(res, 'object.data.profile.secret_theme', false)) {
                    const t = [...themes];
                    t.push('Pink');
                    setThemes(t);
                }
            });
        }
        setInit(false);

        userPerms.write && RolesService.getRoles().then((roles) => {
            setRoles(roles.data);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Manually update select fields for now
        if (user) {
            const updatedForm = {
                ...userForm.get(),
                roleSelect: (wantRoles ? !!user.roles.length : true)
            };
            userForm.set(updatedForm);
            setTimeout(() => {
                setValid((user.email || '').length > 0 &&
                        (user.username || '').length > 0 &&
                        (user.name || '').length > 0 && checkValidity(updatedForm));
            });
        }
        setChanges(!_isEqual(user, OGUser));
    }, [user, OGUser, wantRoles, userForm]);

    const getRoleType = useCallback(() => {
        if (user.username === 'admin') return 'Privileged';
        if (isUpdate && user.type !== 'brc') return `${user.type} Authorization`;

        let priv = false;
        for (let i = user.roles.length; i--;) {
            const r = roles.findIndex((v) => v.name === user.roles[i]);
            if (roles[r]?.access === 'privileged') {
                priv = true;
                break;
            }
        }

        return priv ? 'Privileged' : 'Standard';
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.username, user.roles, user.type, roles, isUpdate]);

    const handleUpdateUserData = (e, validID, valid) => {
        userForm.set({ ...userForm.get(), [validID]: valid });

        setUser({
            ...user,
            [e.target.name]: e.target.value,
        });
    };

    const handleUpdateUserRoles = (e) => {
        // userForm.set({ ...userForm.get(), roleSelect: (user.username === 'admin' ? true : !!e.target.value) });
        userForm.set({ ...userForm.get(), roleSelect: (wantRoles ? !!user.roles.length : true) });
        setUser({
            ...user,
            roles: e.target.value,
        });
    };

    const handleUpdateUserProfile = (e) => {
        const u = { ...user };
        u.data.profile[e.target.name] = e.target.value;
        setUser(u);
    };

    const handleUpdateDates = (e, format) => {
        if (!e.target) return;
        const v = e.target.value;

        const u = { ...user };
        u.data.profile.date = { ...(u.data.profile.date || {}), [format]: v };
        setUser(u);
    };

    const getDateFormat = format => {
        return _get(user, `data.profile.date.${format}`, theme.date[format]);
    };

    const handleCreateUser = () => {
        if (isUpdate) {
            toast.info('Updating user...');
            UsersService.updateUser(routeParams.username, user).then(() => {
                toast.success('User updated successfully');
                // if self update theme
                if (user.username === User().username) {
                    changeTheme(TranslateTheme(_get(user, 'data.profile.colorscheme', 'dark')));
                    changeDateFormat(_get(user, 'data.profile.date', {}));
                }
                updateProfile('home', user.data.profile.home);
                if (location.state && location.state.fromUsers) {
                    navigate('/system/users_roles', { state: { tab: 0 } });
                } else {
                    navigate(-1);
                }
            }, err => {
                if (err.status === 403) {
                    setUser({
                        ...user,
                        roles: _cloneDeep(OGUser.roles),
                    });
                }
                toast.error(err.error);
                console.error(err);
            });
        } else {
            toast.info('Creating user...');
            UsersService.createUser(user).then(() => {
                toast.success('User created successfully');
                navigate('/system/users_roles', { state: { tab: 0 } });
            }, err => {
                console.error(err);
                if (err.status === 409) {
                    toast.error('Username is already taken...');
                } else {
                    toast.error(err.error);
                }
            });
        }
    };

    const handleCancel = () => {
        if (location.state && location.state.fromUsers) {
            navigate('/system/users_roles', { state: { tab: 0 } });
        } else {
            navigate(-1);
        }
    };

    const handleChangePassword = () => {
        navigate('/system/users_roles/password/' + user.username);
    };

    const handleRemoveRole = (i) => {
        const u = { ...user };
        u.roles.splice(i, 1);

        userForm.set({ ...userForm.get(), roleSelect: (wantRoles ? !!user.roles.length : true) });
        // userForm.set({ ...userForm.get(), roleSelect: (user.username === 'admin' ? true : !!u.roles.length) });

        setUser(u);
    };

    const handleAccessKeys = () => {
        navigate('/system/configuration/auth/access_keys', { state: { fromUser: true } });
    };

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    return (
        <Grid container spacing={2} direction="row" justifyContent="center" alignItems="center">
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Grid container spacing={5}>
                            <Grid item xs={6}>
                                <Validator
                                    valueType="username"
                                    errorText="Username is invalid"
                                    label="Username"
                                    aria-label="username"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    kick={init}
                                    disabled={isUpdate}
                                    value={user.username}
                                    name="username"
                                    onChange={handleUpdateUserData}
                                />
                                <Validator
                                    valueType="email"
                                    errorText="Email is invalid"
                                    label="Email"
                                    aria-label="email"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    kick={init}
                                    required={true}
                                    allowEmpty={false}
                                    value={user.email}
                                    name="email"
                                    onChange={handleUpdateUserData}
                                />
                                <Validator
                                    errorText="Display name cannot be empty"
                                    label="Display Name"
                                    aria-label="display name"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    kick={init}
                                    value={user.name}
                                    name="name"
                                    onChange={handleUpdateUserData}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Account Type"
                                    aria-label="account type"
                                    value={getRoleType()}
                                    disabled
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                />
                                {wantRoles && <Box>
                                    <FormControl className={classes.select}>
                                        <InputLabel id="role-select-label">Role</InputLabel>
                                        <Select
                                            disabled={!userPerms.write}
                                            labelId="role-select-label"
                                            id="role-select"
                                            value={user.roles}
                                            multiple
                                            onChange={handleUpdateUserRoles}
                                            input={<Input id="role-select-input" />}
                                            renderValue={(rolesSelected) => (
                                                <div className={classes.chips}>
                                                    {rolesSelected.map((v, i) => {
                                                        return <Chip
                                                            disabled={!userPerms.write}
                                                            key={'role-chip-' + i}
                                                            aria-label={v}
                                                            label={v}
                                                            className={classes.chip}
                                                            onMouseDown={e => { e.stopPropagation() }}
                                                            onDelete={() => { handleRemoveRole(i) }}
                                                        />;
                                                    })}
                                                </div>
                                            )}
                                            MenuProps={MenuProps}
                                        >
                                            {roles.map((v, i) => (<MenuItem key={'role-select-' + i} value={v.name}>{v.name} - {v.description}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                </Box>}
                            </Grid>
                            <Grid container item xs={12}>
                                <Grid item xs={6}>
                                    <Box py={2} pr={2}>
                                        <Paper elevation={3} style={{ background: theme.palette.background.paperAlt }}>
                                            <Box p={2}>
                                                <FormControl>
                                                    <Typography varient="h5">Home Page</Typography>
                                                    <RadioGroup row onChange={handleUpdateUserProfile} value={user.data.profile.home}>
                                                        {HOME_PAGES.map((v, i) => {
                                                            return (<FormControlLabel
                                                                key={'home-page-' + i}
                                                                label={v.name}
                                                                aria-label={v.name}
                                                                value={v.value}
                                                                name="home"
                                                                control={<Radio />}
                                                            />);
                                                        })}
                                                    </RadioGroup>
                                                </FormControl>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box py={2} pl={2}>
                                        <Paper elevation={3} style={{ background: theme.palette.background.paperAlt }}>
                                            <Box p={2}>
                                                <FormControl>
                                                    <Typography varient="h5">Color Scheme</Typography>
                                                    <RadioGroup row onChange={handleUpdateUserProfile} value={user.data.profile.colorscheme || ''}>
                                                        {themes.map((v, i) => {
                                                            return (<FormControlLabel
                                                                key={'theme-' + i}
                                                                label={v}
                                                                aria-label={v}
                                                                value={v.toLowerCase()}
                                                                name="colorscheme"
                                                                control={<Radio />}
                                                            />);
                                                        })}
                                                    </RadioGroup>
                                                </FormControl>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}>
                                <Grid item xs={6}>
                                    <Paper elevation={3} style={{ background: theme.palette.background.paperAlt }}>
                                        <Box p={2}>
                                            <FormControl style={{ width: '100%' }}>
                                                <Typography varient="h5">Date Formats
                                                    <a target="_blank" rel="noreferrer noopener" aria-label="date format reference" href="https://momentjs.com/docs/#/displaying/format/">
                                                        <IconButton size="large"><InfoIcon /></IconButton>
                                                    </a>
                                                </Typography>
                                                <Box style={{ marginTop: 20 }}>
                                                    <TextField
                                                        label="Long Date with Time Zone"
                                                        disabled={!userPerms.write}
                                                        fullWidth
                                                        placeholder={'HH:mm:ss MM/DD/YY ZZ'}
                                                        value={getDateFormat('longtz')}
                                                        onChange={e => handleUpdateDates(e, 'longtz')}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">
                                                                {Moment().format(getDateFormat('longtz'))}
                                                            </InputAdornment>
                                                        }}
                                                    />
                                                </Box>

                                                <Box style={{ marginTop: 20 }}>
                                                    <TextField
                                                        label="Long Date"
                                                        disabled={!userPerms.write}
                                                        fullWidth
                                                        placeholder={'HH:mm:ss MM/DD/YY'}
                                                        value={getDateFormat('long')}
                                                        onChange={e => handleUpdateDates(e, 'long')}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">
                                                                {Moment().format(getDateFormat('long'))}
                                                            </InputAdornment>
                                                        }}
                                                    />
                                                </Box>

                                                <Box style={{ marginTop: 20 }}>
                                                    <TextField
                                                        label="Short Date"
                                                        disabled={!userPerms.write}
                                                        fullWidth
                                                        placeholder={'HH:mm MM/DD/YY'}
                                                        value={getDateFormat('short')}
                                                        onChange={e => handleUpdateDates(e, 'short')}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">
                                                                {Moment().format(getDateFormat('short'))}
                                                            </InputAdornment>
                                                        }}
                                                    />
                                                </Box>
                                            </FormControl>
                                        </Box>
                                    </Paper>
                                </Grid>
                                {isUpdate && accessKeysAuthPerms.write && user.username === User().username && <Grid item xs={12}>
                                    <Box style={{ marginTop: 20 }} display="flex" justifyContent="flex-start">
                                        {/*link to create client api tokens*/}
                                        <Link
                                            component="button"
                                            variant="button"
                                            onClick={handleAccessKeys}>
                                        Setup API Access Keys
                                        </Link>
                                    </Box>
                                </Grid>
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end">
                                    {(user.username === User().username || (user.username !== User().username && user.username !== 'admin' && userPerms.override)) && <Box ml={2} display={isUpdate ? '' : 'none'}>
                                        <Button variant="contained" color="primary" onClick={handleChangePassword}>Change Password</Button>
                                    </Box>}
                                    <Box ml={2}><Button variant="contained" color="secondary" aria-label="Cancel" onClick={handleCancel}>Cancel</Button></Box>
                                    <Box ml={2}><Button
                                        variant="contained"
                                        color="primary"
                                        aria-label={isUpdate ? 'Update' : 'Create'}
                                        onClick={handleCreateUser}
                                        disabled={!valid || !changes}
                                    >
                                        {isUpdate ? 'Update' : 'Create'}
                                    </Button></Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default CreateUser;
